import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, Stack, useTheme } from "@mui/material";
import { InternalLink } from "@src/appV2/lib";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { PayInfoLabel } from "../../Pay/PayInfoLabel";
import { OverlappingShiftWorkersAvatars } from "../../WorkWithFriends/components/OverlappingShiftWorkersAvatars";
import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { PostBookingDocumentsPill } from "../Bookability/Documents/PostBookingDocumentsPill";
import { ShiftCardWrapper } from "../Card/Wrapper";
import { BookingCardContentWrapper } from "../CardContentWrapper";
import { useShiftDurationWithBreak } from "../hooks/useShiftDurationWithBreak";
import { ShiftCardSummaryInfo } from "../Open/CardSummaryInfo";
import { type TimeSlot } from "../Open/types";
import { ShiftCardPillsWrapper } from "../PillsWrapper";
import { ShiftQualificationLabel } from "../QualificationLabel";
import { BookingCardSummaryWrapper } from "../SummaryWrapper";
import { TimeSlotPill } from "../TimeSlotPill";
import { CancellationPenaltyFooter } from "./CancellationPenaltyFooter/CancellationPenaltyFooter";

export interface WorkerShiftCardProps {
  href?: string;
  shiftId: string;
  shiftTimeSlot: TimeSlot;
  shiftStart: string;
  shiftEnd: string;
  shiftQualificationName: string;
  shiftQualification?: string;
  shiftHourlyPay?: string;
  shiftTotalPay?: string;
  shiftTotalWithBoostPay?: string;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
  workplaceId: string;
  workplaceName: string;
  workplaceRequiresLunchBreak: boolean;
  workplaceTimezone: string;
  isBookingRequest?: boolean;
  pendingPostBookingDocumentsCount?: number;
}

export function WorkerShiftCard(props: WorkerShiftCardProps) {
  const {
    workplaceId,
    workplaceName,
    workplaceTimezone,
    shiftId,
    shiftTimeSlot,
    shiftStart,
    shiftEnd,
    shiftQualificationName,
    shiftQualification,
    shiftHourlyPay,
    shiftTotalPay,
    shiftTotalWithBoostPay,
    workersForOverlappingShiftSlot,
    workplaceRequiresLunchBreak,
    href,
    isBookingRequest,
    pendingPostBookingDocumentsCount = 0,
  } = props;

  const hasPendingPostBookingDocuments = pendingPostBookingDocumentsCount > 0;

  const theme = useTheme();

  const shiftStartDate = useMemo(() => parseISO(shiftStart), [shiftStart]);
  const shiftEndDate = useMemo(() => parseISO(shiftEnd), [shiftEnd]);

  const currentDate = useCurrentDate(60 * 1000);
  const minutesElapsed = calculateMinutesElapsed(shiftStartDate, currentDate);

  const { totalDurationInHours, mandatoryBreakDurationInMinutes, requiresBreak } =
    useShiftDurationWithBreak({
      startDate: shiftStartDate,
      endDate: shiftEndDate,
      workplaceId,
      requiresLunchBreak: workplaceRequiresLunchBreak,
      minutesElapsed,
    });

  const showOverlappingShiftWorkersAvatars =
    isDefined(workersForOverlappingShiftSlot) &&
    (workersForOverlappingShiftSlot.friends.length > 0 ||
      workersForOverlappingShiftSlot.recentColleagues.length > 0 ||
      workersForOverlappingShiftSlot.otherWorkers.length > 0);

  const cardContent = (
    <BookingCardContentWrapper sx={{ gap: 1 }}>
      <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
        <ShiftCardPillsWrapper>
          <TimeSlotPill timeSlot={shiftTimeSlot} />
          {hasPendingPostBookingDocuments && (
            <PostBookingDocumentsPill count={pendingPostBookingDocumentsCount} shiftId={shiftId} />
          )}
        </ShiftCardPillsWrapper>

        <PayInfoLabel
          hourlyPay={shiftHourlyPay}
          totalPay={shiftTotalPay}
          totalWithBoostPay={shiftTotalWithBoostPay}
        />
      </BookingCardSummaryWrapper>

      <ShiftCardSummaryInfo
        boldDay
        sx={{ flex: 1 }}
        dateRange={{ startDate: shiftStartDate, endDate: shiftEndDate }}
        timezone={workplaceTimezone}
        deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
        durationInHours={totalDurationInHours}
        breakDurationInMinutes={mandatoryBreakDurationInMinutes}
        requiresBreak={requiresBreak}
        workplaceName={workplaceName}
        minutesElapsed={minutesElapsed}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={3}
        sx={{ marginTop: 3, minHeight: (theme) => theme.size?.avatar.small }}
      >
        <ShiftQualificationLabel qualificationText={shiftQualificationName} />

        {showOverlappingShiftWorkersAvatars && (
          <OverlappingShiftWorkersAvatars
            overlappingShiftWorkers={workersForOverlappingShiftSlot}
            profileImageSizeInPx={32}
          />
        )}
      </Stack>
    </BookingCardContentWrapper>
  );

  return (
    <ShiftCardWrapper sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      {isDefined(href) ? (
        <CardActionArea component={InternalLink} to={href} sx={{ flexGrow: 1 }}>
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}
      {!isBookingRequest && (
        <Stack
          spacing={3}
          sx={{
            backgroundColor: theme.palette.background.tertiary,
            borderTop: `1px solid ${theme.palette.border?.primary ?? theme.palette.divider}`,
            "&:empty": { display: "none" },
            paddingX: 6,
            paddingY: 4,
          }}
        >
          <CancellationPenaltyFooter
            shiftId={shiftId}
            shiftStart={shiftStart}
            workplaceId={workplaceId}
            qualification={shiftQualification}
          />
        </Stack>
      )}
    </ShiftCardWrapper>
  );
}
